import { createFileRoute, isNotFound, Link } from '@tanstack/react-router';
import { Footer } from './-components/footer';
import { Header } from './-components/header';
import { CampaignHero } from './-components/campaign-hero';
import { useTranslation } from '@/lib/i18n';
import { ChevronLeftIcon } from 'lucide-react';
import { Campaign } from './-components/campaign';
import { CampaignDetails } from './-components/campaign-details';
import { CampaignMetadata } from './-components/campaign-metadata';
import { CampaignImageGallery } from './-components/campaign-image-gallery';
import {
	portalAuthorOptions,
	portalCampaignMetadataOptions,
	portalCampaignOptions,
	portalCampaignsAggregatesOptions,
	portalMetadataOptions,
} from '@/lib/queries';
import { preloadImageOnClient, heroImageSize } from '@worldcoo/ui/lib/utils';
import { Suspense, useEffect } from 'react';
import { Seo } from './-components/seo';
import { generateImageSrc, getLogoImageSize } from '@/lib/shared';
import { usePortalCampaignMetadataQuery } from '@/lib/queries';
import { MobileDonateButton } from './-components/mobile-donate-button';
import { useEphemeralStore } from '@/lib/app-store';

export const Route = createFileRoute('/$portalId/$campaignId/')({
	loader: ({ context, params }) => {
		context.queryClient.ensureQueryData(
			portalMetadataOptions({
				locale: context.locale,
				portalId: context.portalId,
			}),
		);

		context.queryClient
			.ensureQueryData(portalAuthorOptions({ locale: context.locale, portalId: context.portalId }))
			.then((data) => {
				if ('notFound' in data) return;
				if (!data.logo?.location) return;
				preloadImageOnClient(
					generateImageSrc(data.logo.location, {
						height: getLogoImageSize(context.portalId).height,
						width: (data.logo.width / data.logo.height) * getLogoImageSize(context.portalId).height,
					}),
				);
			});

		context.queryClient.ensureQueryData(
			portalCampaignMetadataOptions({
				locale: context.locale,
				portalId: context.portalId,
				campaignId: params.campaignId,
			}),
		);

		context.queryClient.ensureQueryData(portalCampaignsAggregatesOptions({ portalId: context.portalId }));

		context.queryClient
			.ensureQueryData(
				portalCampaignOptions({ locale: context.locale, portalId: context.portalId, campaignId: params.campaignId }),
			)
			.then((campaign) => {
				if ('notFound' in campaign) return;
				if (!campaign.media?.location) return;
				preloadImageOnClient(generateImageSrc(campaign.media.location, heroImageSize));
			});
	},
	component: Component,
});

function Component() {
	const { portalId, campaignId } = Route.useParams();
	const { i18n } = useTranslation();

	const pagesLoaded = useEphemeralStore((store) => store.pagesLoaded);
	const setPagesLoad = useEphemeralStore((store) => store.setPagesLoaded);
	useEffect(() => {
		if (pagesLoaded.includes(`campaign-${campaignId}`)) return;
		const timeout = setTimeout(() => setPagesLoad([...pagesLoaded, `campaign-${campaignId}`]), 1500);
		return () => clearTimeout(timeout);
	}, [campaignId, pagesLoaded, setPagesLoad]);

	return (
		// We're wrapping everything with the secondary color
		// because we need the sticky donate button at the same DOM level so it can work properly
		// and also we want to avoid white-on-grey color when showing to donate button at bottom in non-sticky mode,
		// hence the secondary color
		<div className="bg-secondary">
			<Header />
			<Suspense>
				<SuspensedHead />
			</Suspense>
			<div className="bg-background pb-6">
				<nav className="mx-auto w-full max-w-[1366px]">
					<Link
						to="/$portalId"
						params={{ portalId }}
						search={(prev) => prev}
						className="flex w-fit items-center gap-2 p-6"
					>
						<ChevronLeftIcon />
						{i18n.actions.backToHome}
					</Link>
				</nav>

				<section className="mx-auto w-full max-w-[1366px]">
					<CampaignHero campaignId={campaignId} />
				</section>

				<CampaignImageGallery campaignId={campaignId} />
			</div>
			<div className="bg-secondary border-t">
				<div className="mx-auto w-full max-w-[1366px] px-6 pt-12 lg:pt-16">
					<CampaignMetadata campaignId={campaignId} />
				</div>
				<main className="mx-auto flex w-full max-w-[1366px] flex-col-reverse gap-12 px-6 pt-6 pb-6 lg:flex-row lg:gap-24 lg:pb-16">
					<Campaign campaignId={campaignId} />
					{/**
					 * Add min height + stick to bottom instead to top so it can
					 * show the ngos in case the screen is too small
					 **/}
					<aside className="bottom-12 w-full flex-1 self-end lg:sticky lg:min-h-[calc(100dvh-150px)]">
						<CampaignDetails campaignId={campaignId} />
					</aside>
				</main>
			</div>
			<MobileDonateButton campaignId={campaignId} />
			<Footer />
		</div>
	);
}

function SuspensedHead() {
	const { campaignId } = Route.useParams();
	const metadata = usePortalCampaignMetadataQuery(campaignId);
	if (isNotFound(metadata)) return null;
	return (
		<Seo
			title={metadata.title}
			description={metadata.description}
			image={metadata.image?.location ? generateImageSrc(metadata.image.location) : undefined}
		/>
	);
}
